import { ChevronRight } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  evidenceQuote,
  overviewSections,
} from '@/features/deal-review/deal-lead-fields'
import type { OverviewField } from '@/features/deal-review/deal-lead-fields'
import { InlineFieldValue } from '@/components/custom/deal-review/inline-field'
import { correctableFor } from '@/features/deal-review/deal-lead-corrections'
import type { DealLeadDetail } from '@/types/api'

export function ExtractedFields({ lead }: { lead: DealLeadDetail }) {
  const fields = overviewSections(lead).flatMap((section) => section.fields)

  if (fields.length === 0) return null

  const absent = fields.filter((field) => field.isAbsent)

  return (
    <Collapsible defaultOpen className="border-y border-primary/10">
      <CollapsibleTrigger className="group/drawer flex w-full items-center justify-between gap-3 px-0.5 py-3.5 text-left hover:bg-primary/[0.02]">
        <Typography
          variant="body"
          className="text-sm font-normal leading-[1.2] text-primary"
        >
          All extracted fields
        </Typography>
        <span className="flex items-center gap-2 text-primary/70">
          <Typography as="span" variant="body-sm">
            {fields.length}
          </Typography>
          <ChevronRight
            aria-hidden="true"
            className="size-3.5 transition-transform group-data-[state=open]/drawer:rotate-90"
          />
        </span>
      </CollapsibleTrigger>

      <CollapsibleContent className="pb-3.5">
        <TooltipProvider delayDuration={150}>
          {fields.map((field) => (
            <FieldRow key={field.label} lead={lead} field={field} />
          ))}
        </TooltipProvider>

        {absent.length > 0 && (
          <Typography
            variant="body-sm"
            className="mt-2.5 text-xs leading-normal text-primary/70"
          >
            Not stated in the source:{' '}
            {absent.map((field) => field.label).join(', ')}.
          </Typography>
        )}

        {lead.capabilities.canCorrect && (
          <Typography
            variant="body-sm"
            className="mt-2.5 text-xs leading-normal text-primary/70"
          >
            Click a value to correct it. Corrections are kept against the lead;
            the screening is not re-run.
          </Typography>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

function FieldRow({
  lead,
  field,
}: {
  lead: DealLeadDetail
  field: OverviewField
}) {
  const correctable = correctableFor(field.evidenceKey)
  const quote = field.isAbsent ? null : evidenceQuote(lead, field.evidenceKey)
  const valueClass = cn(
    'border-b border-dotted border-primary/55 text-sm leading-[1.45] tabular-nums',
    field.isAbsent && 'border-transparent text-primary/55'
  )
  const display = field.isAbsent ? (
    <>
      <span aria-hidden="true">—</span>
      <span className="sr-only">{field.value}</span>
    </>
  ) : (
    field.value
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="grid grid-cols-[168px_minmax(0,1fr)] items-baseline gap-3.5 py-1.5">
          <Typography
            variant="body-sm"
            className="leading-[1.2] text-primary/70"
          >
            {field.label}
            {field.derived && (
              <span className="ml-1.5 text-primary/70">computed</span>
            )}
          </Typography>
          <div className="min-w-0">
            {correctable ? (
              <InlineFieldValue
                lead={lead}
                field={correctable}
                display={display}
                className={valueClass}
              />
            ) : (
              <span
                tabIndex={0}
                className={cn(
                  valueClass,
                  'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
              >
                {display}
              </span>
            )}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        // The 168px label track plus its gap.
        alignOffset={182}
        className={cn('max-w-80', quote && 'italic')}
      >
        {quote ? (
          <>&ldquo;{quote}&rdquo;</>
        ) : (
          'No excerpt captured for this field.'
        )}
      </TooltipContent>
    </Tooltip>
  )
}
